import { PhotoData } from "../../photo/interfaces/dto";

declare var pm: PmInterface;

export function buildPhotoOutput(
	photo: PhotoData,
	wrapper: HTMLElement,
	gridItemToClone: HTMLElement,
	supporterBadgeToClone: HTMLElement | null,
	proBadgeToClone: HTMLElement | null,
	superbackerBadgeToClone: HTMLElement | null,
	insertMode: "append" | "prepend" = "append",
) {
	const { findHtmlEl, findLinkEl } = pm.utils.dom;

	const adId = photo.adId ? photo.adId : 0;
	const isAd = adId > 0;

	const videoUrl = photo.videoUrl;
	const isVideo = videoUrl ? true : false;

	const item = gridItemToClone.cloneNode(true);
	if (!(item instanceof HTMLElement)) return;

	const imageWrapper = findHtmlEl(item, ".grid-image-wrapper");
	if (!imageWrapper) return;

	const img = findHtmlEl(item, ".grid-image");
	const metadata = findHtmlEl(item, ".gallery-metadata");
	const adInfo = findHtmlEl(item, ".spc-info");
	const nicenameEl = findHtmlEl(item, ".gallery-nicename");
	const profileLink = findLinkEl(item, ".gallery-nicename a");
	const caption = findHtmlEl(item, ".photo-caption");
	let photoClassName =
		"grid-image" + (!isAd ? " has-context-menu photo-popup-trigger" : "");
	const membershipBadge = findHtmlEl(
		item,
		".gallery-nicename .svg-icon-membership-badge",
	);

	let clonedMembershipBadge;

	let catIdsStr = "";

	if (photo.categories.length) {
		catIdsStr = photo.categories.join(", ");
		imageWrapper.dataset.categoryIds = catIdsStr;
	} else {
		imageWrapper.dataset.cateogryIds = "";
	}

	imageWrapper.dataset.largeUrl = photo.urlLarge;

	if (videoUrl) {
		imageWrapper.dataset.videoUrl = videoUrl;
	}

	if (photo.featured) {
		photoClassName += " is-featured";
	}

	if (photo.potd) {
		photoClassName += " assigned-to-potd";
	}

	if (photo.isLiked) {
		photoClassName += " is-liked";
	}

	if (img) {
		if (img.parentNode) {
			img.parentNode.removeChild(img);
		}
	}

	const image = document.createElement("img");

	if (isAd) {
		// Should be before image.src attr is set.
		image.addEventListener("load", function () {
			pm.event.emit("ad_loaded", { id: adId });
		});
	}

	const imageAlt = photo.imageTitle
		? photo.imageTitle
		: "Photo by " + photo.authorDisplayName;

	image.width = photo.width;
	image.height = photo.height;
	image.className = photoClassName;
	image.src = photo.url;
	image.alt = imageAlt;
	image.dataset.albumSlug = photo.album;
	image.setAttribute("draggable", "true");

	if (isAd) {
		image.addEventListener("load", function () {
			pm.event.emit("ad_loaded", { id: adId });
		});

		const imageLink = document.createElement("a");

		imageLink.href = photo.attachmentUrl;
		imageLink.target = "_blank";
		imageLink.appendChild(image);

		imageWrapper.insertBefore(imageLink, imageWrapper.firstChild);

		metadata?.parentElement?.removeChild(metadata);

		if (profileLink) {
			if (profileLink.parentNode) {
				profileLink.parentNode.removeChild(profileLink);
			}
		}
	} else {
		if (adInfo) {
			adInfo.parentNode?.removeChild(adInfo);
		}

		if (isVideo && metadata) {
			metadata.innerHTML =
				'<span class="play-icon"><i class="wpbff wpbff-caret-right"></i></span> Video';
		} else {
			const apertureSpan = findHtmlEl(metadata, ".aperture");

			if (apertureSpan) {
				if (photo.aperture && "0" !== photo.aperture) {
					apertureSpan.textContent = "F/" + photo.aperture;
				} else {
					apertureSpan.parentElement?.removeChild(apertureSpan);
				}
			}

			const shutterSpeedSpan = findHtmlEl(metadata, ".shutter-speed");

			if (shutterSpeedSpan) {
				if (photo.shutterSpeed && "0" !== photo.shutterSpeed) {
					shutterSpeedSpan.textContent = photo.shutterSpeed;
				} else {
					shutterSpeedSpan.parentElement?.removeChild(shutterSpeedSpan);
				}
			}

			const isoSpan = findHtmlEl(metadata, ".iso");

			if (isoSpan) {
				if (photo.iso && "0" !== photo.iso) {
					isoSpan.textContent = "ISO " + photo.iso;
				} else {
					isoSpan.parentElement?.removeChild(isoSpan);
				}
			}
		}

		imageWrapper.insertBefore(image, imageWrapper.firstChild);

		if (profileLink) {
			/**
			 * This will also remove the trailing slash from pm.siteUrl
			 * @link https://stackoverflow.com/questions/6680825/return-string-without-trailing-slash/#answer-6680877
			 */
			profileLink.href =
				pm.siteUrl.replace(/\/$/, "") +
				"/profile/" +
				photo.authorNicename +
				"/";

			profileLink.textContent = photo.authorNicename;
		}

		if (caption) {
			caption.innerHTML = photo.description;
		}
	}

	const likeButton = findHtmlEl(item, ".like-photo");

	const buttonTextEl = likeButton
		? findHtmlEl(likeButton, ".button-text")
		: null;

	if (likeButton && wrapper.dataset.useLikeStatus) {
		likeButton.dataset.userId = photo.id.toString();

		if (buttonTextEl) {
			buttonTextEl.textContent = photo.totalLikes > 1 ? "Likes" : "Like";
		}

		if (photo.isLiked) {
			likeButton.classList.add("is-active");
		} else {
			likeButton.classList.remove("is-active");
		}
	}

	const deleteButton = findHtmlEl(item, ".delete-photo");

	if (deleteButton) {
		deleteButton.dataset.photoId = photo.id.toString();
	}

	imageWrapper.dataset.photoId = photo.id.toString();
	imageWrapper.dataset.publishedDate = photo.date;
	imageWrapper.dataset.largeUrl = photo.urlLarge;
	imageWrapper.dataset.attachmentUrl = photo.attachmentUrl;
	imageWrapper.dataset.camera = photo.camera;
	imageWrapper.dataset.cameraSlug = photo.cameraSlug;
	imageWrapper.dataset.cameraUrl = photo.cameraUrl;
	imageWrapper.dataset.cameraAlias = photo.cameraAlias;
	imageWrapper.dataset.editableCamera = photo.editableCamera ? "1" : "0";
	imageWrapper.dataset.focalLength = photo.focalLength;
	imageWrapper.dataset.editableFocalLength = photo.editableFocalLength
		? "1"
		: "0";
	imageWrapper.dataset.totalLikes = photo.totalLikes.toString();

	/**
	 * Handle the `gulp` running mode.
	 *
	 * When the local site is running gulp, the photo.attachmentUrl is not converted to port-structured URL.
	 * That causes the photo popup fail to open because the actual URL is using port-structured URL,
	 * but the photo.attachmentUrl is not.
	 */
	if (photo.attachmentUrl.indexOf(pm.siteUrl) !== 0) {
		// Let's assume this is a local site running gulp.
		if (photo.attachmentUrl.includes(".local/photo-")) {
			const attachmentUrlSplits = photo.attachmentUrl.split(".local/photo-");

			photo.attachmentUrl =
				pm.utils.str.stripTrailingSlash(pm.siteUrl) +
				"/photo-" +
				attachmentUrlSplits[1];
		}
	}

	imageWrapper.dataset.attachmentUrl = photo.attachmentUrl;

	const locationEl = findHtmlEl(imageWrapper, ".gallery-location");

	if (locationEl) {
		if (isVideo) {
			locationEl.parentElement?.removeChild(locationEl);
		} else {
			locationEl.innerHTML = photo.address;
		}
	}

	const galleryAuthor = findHtmlEl(imageWrapper, ".gallery-author");

	if (galleryAuthor) {
		galleryAuthor.textContent = photo.authorDisplayName;
		galleryAuthor.dataset.authorId = photo.authorId.toString();
		galleryAuthor.dataset.authorNicename = photo.authorNicename;
		galleryAuthor.dataset.authorDisplayName = photo.authorDisplayName;
		galleryAuthor.dataset.authorMembershipType = photo.authorMembershipType;
		galleryAuthor.dataset.authorAvatarUrl = photo.authorAvatarUrl;
		galleryAuthor.dataset.authorProfileUrl = photo.authorProfileUrl;
	}

	const headerAvatar = findHtmlEl(imageWrapper, ".grid-item-header .avatar");

	if (headerAvatar instanceof HTMLImageElement) {
		headerAvatar.src = photo.authorAvatarUrl;
	}

	const photoCaptionEl = findHtmlEl(imageWrapper, ".photo-caption");

	if (photoCaptionEl && photo.description) {
		photoCaptionEl.innerHTML = pm.utils.str.wpautop(photo.description, true);
	}

	const rawPhotoCaptionEl = findHtmlEl(imageWrapper, ".raw-photo-caption");

	if (rawPhotoCaptionEl && photo.description) {
		rawPhotoCaptionEl.innerHTML = photo.description;
	}

	if (photo.authorMembershipType) {
		// Only create the badge if it doesn't exist.
		if (!membershipBadge) {
			if (
				nicenameEl &&
				(supporterBadgeToClone || proBadgeToClone || superbackerBadgeToClone)
			) {
				if (
					photo.authorMembershipType === "superbacker" &&
					superbackerBadgeToClone
				) {
					clonedMembershipBadge = superbackerBadgeToClone.cloneNode(true);
				} else if (photo.authorMembershipType === "pro" && proBadgeToClone) {
					clonedMembershipBadge = proBadgeToClone.cloneNode(true);
				} else {
					if (supporterBadgeToClone) {
						clonedMembershipBadge = supporterBadgeToClone.cloneNode(true);
					}
				}

				if (clonedMembershipBadge) {
					nicenameEl.appendChild(clonedMembershipBadge);
				}
			}
		}
	} else {
		// Remove badge if it exists.
		if (membershipBadge && membershipBadge.parentNode) {
			membershipBadge.parentNode.removeChild(membershipBadge);
		}

		if (nicenameEl) {
			nicenameEl.textContent = photo.authorNicename;
		}
	}

	const masonryBreak = findHtmlEl(wrapper, ".flexmasonry-break");

	if (insertMode === "append") {
		if (masonryBreak) {
			wrapper.insertBefore(item, masonryBreak);
		} else {
			wrapper.appendChild(item);
		}
	} else {
		if (!wrapper.children.length) {
			wrapper.appendChild(item);
		} else {
			wrapper.insertBefore(item, wrapper.firstChild);
		}
	}

	if (isAd) {
		item.classList.add("is-spc");
		item.dataset.spcId = photo.adId.toString();
	}

	if (isVideo) {
		item.classList.add("is-video");
	} else {
		imageWrapper.removeAttribute("data-video-url");
	}
}
